import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { List, Space, Divider, Tag, Button, Card } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { PageContainer } from '@ant-design/pro-layout';
import { useQuery, useMutation } from '@apollo/client';
import {
  GET_USERS_WITH_PAGINATION,
  TOGGLE_DISABLED_USER,
} from '../../../gql/users';
import SearchUser from './SearchUsers';
import useListActions from 'hooks/useListActions';

const deptFac = ({ department, faculty }) => {
  if (department) {
    return (
      <div>{`${department?.abbreviation}@${department?.faculty.abbreviation}`}</div>
    );
  }
  if (faculty) {
    return <div>{faculty.abbreviation}</div>;
  }
  return <></>;
};

const Users = () => {
  const { data, loading, refetch } = useQuery(GET_USERS_WITH_PAGINATION, {
    variables: { state: 'ALL', current: 1, pageSize: 1 },
  });
  const [refetchLoading, setRefetchLoading] = useState(false);
  const [toggleDisabled] = useMutation(TOGGLE_DISABLED_USER, {
    refetchQueries: ({
      data: {
        toggleDisabledUser: { id },
      },
    }) => [{ query: GET_USERS_WITH_PAGINATION, variables: { ids: [id] } }],
    onError: (err) => {
      console.log(err);
    },
  });
  const [search, setSearch] = useState({
    netId: '',
    faculty: '',
    department: '',
    role: '',
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });

  const listActions = useListActions({
    edit: { url: ({ id }) => `/system-setting/users/${id}` },
    toggleDisabled: {
      onClick: ({ id }) => {
        toggleDisabled({ variables: { id } });
      },
    },
  });
  useEffect(() => {
    async function fetchData() {
      setRefetchLoading(true);
      await refetch({
        current: 1,
        pageSize: pagination.pageSize,
        department: search?.department,
        faculty: search?.faculty,
        role: search?.role,
        netId: search?.netId,
      });
      setRefetchLoading(false);
    }
    fetchData();
    setPagination(({ pageSize }) => ({
      current: 1,
      pageSize,
    }));
  }, [search, refetch, pagination.pageSize]);
  const users = data?.usersWithPagination?.nodes;

  return (
    <PageContainer
      loading={loading}
      header={{
        title: 'Users',
        extra: [
          <Button key="add" type="primary">
            <Link to="/system-setting/users/add">
              <PlusOutlined /> Add
            </Link>
          </Button>,
        ],
      }}
    >
      <SearchUser search={search} setSearch={setSearch} />
      <Card bordered={false}>
        <List
          itemLayout="horizontal"
          loading={refetchLoading}
          dataSource={loading ? [] : users}
          pagination={{
            total: data?.usersWithPagination?.totalCount,
            onChange: async (page, pageSize) => {
              setPagination({
                current: page,
                pageSize,
              });
              setRefetchLoading(true);
              await refetch({
                current: page,
                pageSize,
                department: search?.department,
                faculty: search?.faculty,
                role: search?.role,
                netId: search?.netId,
              });
              setRefetchLoading(false);
            },
            current: pagination.current,
            pageSize: pagination.pageSize,
          }}
          renderItem={(item) =>
            item.deleted ? (
              ''
            ) : (
              <List.Item actions={listActions({ item })}>
                <List.Item.Meta
                  title={`${item.name} (${item.netId})`}
                  description={
                    <>
                      {deptFac({
                        department: item.department,
                        faculty: item.faculty,
                      })}
                      <Space split={<Divider type="vertical" />}>
                        {item.roles.map(({ role, faculty, department }) =>
                          role.disabled ? null : (
                            <Link
                              to={`/system-setting/roles/${role.id}`}
                              key={role.name}
                            >
                              {`${role.name}${
                                department
                                  ? `@${department.abbreviation}`
                                  : faculty
                                  ? `@${faculty.abbreviation}`
                                  : ''
                              }`}
                            </Link>
                          )
                        )}
                      </Space>
                    </>
                  }
                />
                {item.disabled ? (
                  <Tag>Disabled</Tag>
                ) : item.deleted ? (
                  <Tag color="red">Deleted</Tag>
                ) : (
                  <Tag color="green">Active</Tag>
                )}
              </List.Item>
            )
          }
        ></List>
      </Card>
    </PageContainer>
  );
};

export default Users;
